import { isNullOrEmpty } from 'common-utils';
import { IDetectPackageDependenciesResult } from 'dto/packages-dto';
import { RepositoryType } from 'dto/repository.dto';
import { ISnapshotProps } from 'dto/software-snapshot-dto';
import { toNumber } from 'lodash';
import { observer } from 'mobx-react';
import { TableUtils } from 'pages/private/components/table-utils';
import React from 'react';
import { useSnapshotStore } from 'store';
import { IDisplaySnapshotProps } from 'store/snapshot.store';
import { AdvanceTable, IBasicTableColumn, SelectFormItem, Typography } from 'ui-lib';

const deploymentUnitTypes = ['SOFTWARE_UPDATE']; // full list is 'REMOTE_COMMAND', 'SOFTWARE_UPDATE', 'CONFIGURATION_UPDATE'
const deploymentUnitTypeOptions = deploymentUnitTypes.map((item) => ({ label: item, value: item }));


export interface IAddReleaseStepProps {
    accountUuid: string;
    selectedReleaseId?: number;
    addReleaseToDeployment: (releaseId: number) => void;
}

export const AddReleaseStep = observer((props: IAddReleaseStepProps): JSX.Element => {
    const { accountUuid, selectedReleaseId, addReleaseToDeployment } = props;

    const snapshotStore = useSnapshotStore();
    const releaseList = React.useMemo<IDisplaySnapshotProps[]>(() => {
        if (!accountUuid) {
            return undefined;
        }
        return snapshotStore.getSnapshotsByAccountUuid(accountUuid);
    }, [accountUuid, snapshotStore.displayPublishedSnapshots]);

    const releasesColumns: IBasicTableColumn[] = [
        TableUtils.createBasicFilterColumn({ title: 'Software release name', key: 'label' }),
        TableUtils.createBasicEnumFilterColumn({
            title: 'Release type',
            key: 'type',
            filters: [RepositoryType.VAR, RepositoryType.CLIENT].map((type) => ({ text: type, value: type })),
        }),
        TableUtils.createBasicFilterColumn({ title: 'Version', key: 'version' }),
        TableUtils.createBasicFilterTimeColumn({ title: 'Updated', key: 'updatedAt', defaultSortOrder: 'descend' }),
    ];

    return (
        <div>
            <SelectFormItem
                code="4"
                label="Type"
                placeholder="Select a deployment unit type"
                selectedValue={deploymentUnitTypes[0]}
                dataSource={deploymentUnitTypeOptions}
            />
            <AdvanceTable
                title="Add deployment units"
                loadingState={!releaseList}
                table={{
                    columns: releasesColumns,
                    dataSource: releaseList,
                    rowSelection: {
                        type: 'radio',
                        defaultSelectedRowKeys: selectedReleaseId ? [selectedReleaseId] : [],
                        getCheckboxProps: (record: any) => {
                            return {
                                name: record['label'],
                                disabled: record.disabled
                            };
                        },
                        onChange: (selectedRowKeys: number[]): void => {
                            if (isNullOrEmpty(selectedRowKeys)) {
                                return;
                            }
                            // selectedRowKeys is always 1
                            addReleaseToDeployment(selectedRowKeys[0]);
                        }
                    },
                    rowKey: 'id',
                }}
            />
        </div>
    );
});